import { Button, DatePicker, FormField } from '@dev-spendesk/grapes';
import { addDays, addYears, startOfDay } from 'date-fns';
import { useState } from 'react';

import PlaceAutocomplete from 'common/components/PlaceAutocomplete/PlaceAutocomplete';
import { type Location } from 'common/components/PlaceAutocomplete/location';
import { useTranslation } from 'common/hooks/useTranslation';
import { useCompany } from 'modules/app/hooks/useCompany';
import { useUser } from 'modules/app/hooks/useUser';
import {
  NotificationType,
  useNotifications,
} from 'modules/app/notifications/hooks/useNotifications';
import { routeFor, routes } from 'src/core/constants/routes';
import history from 'src/core/history';

import {
  transformToPayload,
  usePropertiesSearchMutation,
} from '../../hooks/usePropertiesSearchMutation';

export const MIN_SEARCH_DATE = startOfDay(new Date());
export const MAX_SEARCH_DATE = addYears(MIN_SEARCH_DATE, 1);

export const StaysSearch = () => {
  const { t } = useTranslation('global');
  const company = useCompany();
  const user = useUser();
  const propertiesSearchMutation = usePropertiesSearchMutation();
  const { pushNotif } = useNotifications();

  const [checkin, setCheckin] = useState<Date | undefined>();
  const [checkout, setCheckout] = useState<Date | undefined>();
  const [location, setLocation] = useState<Location | undefined>();

  // Calculate minimum checkout date: tomorrow if no checkin selected, otherwise day after checkin
  const minCheckoutDate = checkin
    ? addDays(checkin, 1)
    : addDays(MIN_SEARCH_DATE, 1);

  const handleCheckinChange = (value?: Date) => {
    setCheckin(value);
    // If checkout is before or equal to the new checkin date, reset checkout
    if (checkout && value && checkout <= value) {
      setCheckout(undefined);
    }
  };

  const handleSearchSubmit = async () => {
    if (!location || !checkin || !checkout) {
      return;
    }
    const response = await propertiesSearchMutation.mutateAsync(
      transformToPayload(location, checkin, checkout),
    );
    if (response.statusCode === 201) {
      history.push(
        routeFor(routes.TRAVEL_SEARCH.path, {
          company: company.id,
        }),
        {
          location,
          checkin,
          checkout,
          searchId: response.body.data.sessionId,
        },
      );
    } else {
      pushNotif({
        type: NotificationType.Danger,
        message: t('misc.errors.unknownError'),
      });
    }
  };

  return (
    <div className="rounded-12 bg-background-secondary-default h-fit px-[208px] py-40">
      <h2 className="heading-xxl mb-16">{t('travel.planTrip')}</h2>
      <p className="text-content-secondary-bg-primary body-l mb-32">
        {t('travel.hotel.search.description')}
      </p>
      <form className="bg-background-primary-default body-m rounded-12 border-border-default flex w-full items-baseline gap-12 border p-16">
        <FormField label={t('travel.hotel.search.place')} className="flex-1">
          <PlaceAutocomplete
            onPlaceSelect={(newLocation) => {
              setLocation(newLocation ?? undefined);
            }}
            placeholder={t('travel.hotel.search.placePlaceholder')}
            language={user.lang}
            requesterCountryCode={company.country}
          />
        </FormField>
        <FormField label={t('travel.hotel.search.checkin')} className="w-1/5">
          <DatePicker
            fit="parent"
            minDate={MIN_SEARCH_DATE}
            maxDate={MAX_SEARCH_DATE}
            value={checkin}
            onChange={handleCheckinChange}
          />
        </FormField>
        <FormField label={t('travel.hotel.search.checkout')} className="w-1/5">
          <DatePicker
            fit="parent"
            placement="bottom-end"
            minDate={minCheckoutDate}
            maxDate={MAX_SEARCH_DATE}
            value={checkout}
            onChange={(value) => setCheckout(value || undefined)}
          />
        </FormField>
        <Button
          className="self-end"
          type="submit"
          text={t('travel.hotel.search.searchButton')}
          iconName="magnifying-glass"
          onClick={handleSearchSubmit}
          isDisabled={!location || !checkin || !checkout}
        />
      </form>
    </div>
  );
};
